import React, { useState } from 'react';
import classNames from 'classnames';

import { formatCount, formatCompactCurrency } from 'data/investors';
import css from './style.scss';

// data/investors' formatters render a missing value as "—" (the search
// card's own convention) — this section wants a truly blank cell instead, so
// every value here is routed through this before rendering rather than
// changing those shared formatters and affecting the search card too.
const blank = (formatted) => (formatted === '—' ? '' : formatted);

// Acquisition/Disposition History — Figma "tab set" (319:26949) + DataLine,
// line=1 (278:19991 / 383:22357), inside the Investor Profile card. Figma's
// DataLine node is only the 4-metric summary row below the tabs — no property
// table — so this stays summary-only.
//
// The two tabs are NOT the same 4 fields relabeled — confirmed against Figma
// (383:22357) and the vendor's own `acquisitions`/`dispositions` shapes:
// Disposition's first column reads "Total Sold (All Time)" (not "Total (All
// Time)"), and its last column is a **36-month** volume
// (`dispositionPriceLast36Months`), not a 12-month one — the vendor's
// `dispositions` block has no `totalDispositionPriceLast12Months` field at
// all, only a 36-month total. Acquisition's last column is genuinely a
// 12-month volume (`acquisitionPriceLast12Months`), since `acquisitions` does
// carry that one. Each tab's own labels + field reads are kept side by side
// below rather than sharing one template, so this asymmetry stays visible
// instead of getting flattened back into a false 1:1 mirror.
const TABS = [
  { key: 'ACQUISITION', label: 'Acquisition History' },
  { key: 'DISPOSITION', label: 'Disposition History' },
];

const Metric = ({ label, value }) => (
  <div className={css.gridMetric}>
    <span className={css.gridMetric__label}>{label}</span>
    <span className={css.gridMetric__value}>{value}</span>
  </div>
);

const History = ({ investor }) => {
  const [tab, setTab] = useState(TABS[0].key);
  const metrics = investor.metrics || {};
  const isAcquisition = tab === 'ACQUISITION';

  const data = isAcquisition
    ? {
        totalLabel: 'Total (All Time)',
        total: metrics.totalAcquisitionCount,
        last12Count: metrics.acquisitionCountLast12Months,
        avgPrice12mo: metrics.avgAcquisitionPriceLast12Months,
        volumeLabel: 'Last 12 Months Volume',
        volume: metrics.acquisitionPriceLast12Months,
      }
    : {
        totalLabel: 'Total Sold (All Time)',
        total: metrics.totalDispositionCount,
        last12Count: metrics.dispositionCountLast12Months,
        avgPrice12mo: metrics.avgDispositionPriceLast12Months,
        volumeLabel: 'Last 36 Months Volume',
        volume: metrics.dispositionPriceLast36Months,
      };

  return (
    <div className={css.section}>
      <div className={css.tabs}>
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={classNames(css.tab, { [css.tabActive]: tab === key })}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className={css.grid}>
        <div className={css.gridRow}>
          <Metric label={data.totalLabel} value={blank(formatCount(data.total))} />
          <Metric label="Last 12 Months" value={blank(formatCount(data.last12Count))} />
          <Metric label="Avg Price (12mo)" value={blank(formatCompactCurrency(data.avgPrice12mo))} />
          <Metric label={data.volumeLabel} value={blank(formatCompactCurrency(data.volume))} />
        </div>
      </div>
    </div>
  );
};

export default History;
